import { useEffect, useState, useCallback } from 'react';
import { Package, FileText, Braces, Code2, Table, Image, Globe, Eye, Trash2, RefreshCw } from 'lucide-react';
import { Card } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { EmptyState } from '../../components/ui/States';
import { ConfirmDialog } from '../../components/ui/Modal';
import { ArtifactModal } from './ArtifactViewer';
import { formatRelativeTime } from '../../lib/utils';
import { toast } from '../../store/uiStore';
import { artifactsService } from '../../services/artifactsService';
import type { Agent, Artifact } from '../../types';
import type { LucideIcon } from 'lucide-react';

const TYPE_ICON: Record<Artifact['type'], LucideIcon> = {
  html: Globe,
  document: FileText,
  json: Braces,
  code: Code2,
  table: Table,
  image: Image,
};

export function ArtifactsTab({ agent, onCountChange }: { agent: Agent; onCountChange?: (n: number) => void }) {
  const [artifacts, setArtifacts] = useState<Artifact[]>([]);
  const [loading, setLoading] = useState(true);
  const [viewing, setViewing] = useState<Artifact | null>(null);
  const [toDelete, setToDelete] = useState<Artifact | null>(null);

  const load = useCallback(async () => {
    const list = await artifactsService.listByAgent(agent.id);
    setArtifacts(list);
    onCountChange?.(list.length);
    setLoading(false);
  }, [agent.id, onCountChange]);

  useEffect(() => {
    load();
  }, [load]);

  async function confirmDelete() {
    if (!toDelete) return;
    const id = toDelete.id;
    setToDelete(null);
    setArtifacts((a) => {
      const next = a.filter((x) => x.id !== id);
      onCountChange?.(next.length);
      return next;
    });
    try {
      await artifactsService.remove(id);
      toast.info('Artefato removido.');
    } catch {
      toast.error('Falha ao remover.');
      load();
    }
  }

  if (loading) return <p className="p-4 text-sm text-slate">Carregando artefatos…</p>;

  if (artifacts.length === 0) {
    return (
      <EmptyState
        icon={Package}
        title="Nenhum artefato ainda"
        description="Artefatos (HTML, documentos, planilhas, código) gerados nas conversas do agente aparecem aqui."
      />
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-xs text-slate">{artifacts.length} artefato(s) gerado(s) nas conversas.</p>
        <button onClick={load} className="flex items-center gap-1 text-xs text-slate hover:text-ink"><RefreshCw className="h-3.5 w-3.5" /> Atualizar</button>
      </div>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        {artifacts.map((art) => {
          const Icon = TYPE_ICON[art.type] ?? FileText;
          return (
            <Card key={art.id} className="flex flex-col p-4">
              <div className="flex items-start gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-navy/10 text-navy">
                  <Icon className="h-5 w-5" />
                </div>
                <div className="min-w-0 flex-1">
                  <h3 className="truncate text-sm font-semibold text-ink">{art.name}</h3>
                  <p className="text-xs text-slate">{art.type} · v{art.version} · {formatRelativeTime(art.createdAt)}</p>
                </div>
              </div>

              <div className="mt-3 max-h-24 overflow-hidden rounded-lg bg-cloud p-2.5 font-mono text-[11px] text-slate">
                <pre className="line-clamp-4 whitespace-pre-wrap break-words">{art.content.slice(0, 240)}</pre>
              </div>

              <div className="mt-3 flex flex-wrap items-center gap-1.5">
                <Button size="sm" variant="secondary" icon={Eye} onClick={() => setViewing(art)}>Abrir</Button>
                <Button size="sm" variant="ghost" icon={Trash2} onClick={() => setToDelete(art)}>Excluir</Button>
              </div>
            </Card>
          );
        })}
      </div>

      <ArtifactModal artifact={viewing} open={!!viewing} onClose={() => setViewing(null)} />
      <ConfirmDialog
        open={!!toDelete}
        onClose={() => setToDelete(null)}
        onConfirm={confirmDelete}
        title="Excluir artefato"
        message={`Excluir "${toDelete?.name}"?`}
      />
    </div>
  );
}
